import { geometry } from '@progress/kendo-drawing';
import { Polar } from './Polar.js';
import { isFunction } from '../utils/functions.js';

const HITTESTAREA = 3;
class Point extends geometry.Point {
    constructor(x, y) {
        super(x, y);
    }
    clone() {
        return new Point(this.x, this.y);
    }
    plus(p) {
        return new Point(this.x + p.x, this.y + p.y);
    }
    minus(p) {
        return new Point(this.x - p.x, this.y - p.y);
    }
    offset(value) {
        return new Point(this.x - value, this.y - value);
    }
    times(s) {
        return new Point(this.x * s, this.y * s);
    }
    normalize() {
        if (this.length() === 0) {
            return new Point();
        }
        return this.times(1 / this.length());
    }
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }
    toString() {
        return '(' + this.x + ',' + this.y + ')';
    }
    lengthSquared() {
        return (this.x * this.x + this.y * this.y);
    }
    middleOf(p, q) {
        return new Point(q.x - p.x, q.y - p.y).times(0.5).plus(p);
    }
    toPolar(useDegrees) {
        let factor = 1;
        if (useDegrees) {
            factor = 180 / Math.PI;
        }
        const a = Math.atan2(Math.abs(this.y), Math.abs(this.x));
        const halfpi = Math.PI / 2;
        const len = this.length();
        if (this.x === 0) {
            // note that the angle goes down and not the usual mathematical convention
            if (this.y === 0) {
                return new Polar(0, 0);
            }
            if (this.y > 0) {
                return new Polar(len, factor * halfpi);
            }
            if (this.y < 0) {
                return new Polar(len, factor * 3 * halfpi);
            }
        }
        else if (this.x > 0) {
            if (this.y === 0) {
                return new Polar(len, 0);
            }
            if (this.y > 0) {
                return new Polar(len, factor * a);
            }
            if (this.y < 0) {
                return new Polar(len, factor * (4 * halfpi - a));
            }
        }
        else {
            if (this.y === 0) {
                return new Polar(len, 2 * halfpi);
            }
            if (this.y > 0) {
                return new Polar(len, factor * (2 * halfpi - a));
            }
            if (this.y < 0) {
                return new Polar(len, factor * (2 * halfpi + a));
            }
        }
    }
    isOnLine(from, to) {
        if (from.x > to.x) { // from must be the leftmost point
            const temp = to;
            to = from;
            from = temp;
        }
        const r1 = new Rect(from.x, from.y).inflate(HITTESTAREA, HITTESTAREA), r2 = new Rect(to.x, to.y).inflate(HITTESTAREA, HITTESTAREA);
        let o1, u1;
        if (r1.union(r2).contains(this)) {
            if (from.x === to.x || from.y === to.y) {
                return true;
            }
            else if (from.y < to.y) {
                o1 = r1.x + (((r2.x - r1.x) * (this.y - (r1.y + r1.height))) / ((r2.y + r2.height) - (r1.y + r1.height)));
                u1 = (r1.x + r1.width) + ((((r2.x + r2.width) - (r1.x + r1.width)) * (this.y - r1.y)) / (r2.y - r1.y));
            }
            else {
                o1 = r1.x + (((r2.x - r1.x) * (this.y - r1.y)) / (r2.y - r1.y));
                u1 = (r1.x + r1.width) + ((((r2.x + r2.width) - (r1.x + r1.width)) * (this.y - (r1.y + r1.height))) / ((r2.y + r2.height) - (r1.y + r1.height)));
            }
            return (this.x > o1 && this.x < u1);
        }
        return false;
    }
    parse(str) {
        const tempStr = str.slice(1, str.length - 1), xy = tempStr.split(','), x = parseInt(xy[0], 10), y = parseInt(xy[1], 10);
        if (!isNaN(x) && !isNaN(y)) {
            return new Point(x, y);
        }
    }
}
class Rect {
    constructor(x, y, width, height) {
        this.x = x || 0;
        this.y = y || 0;
        this.width = width || 0;
        this.height = height || 0;
    }
    contains(point) {
        return ((point.x >= this.x) && (point.x <= (this.x + this.width)) && (point.y >= this.y) && (point.y <= (this.y + this.height)));
    }
    inflate(dx, dy) {
        if (dy === undefined) {
            dy = dx;
        }
        this.x -= dx;
        this.y -= dy;
        this.width += 2 * dx + 1;
        this.height += 2 * dy + 1;
        return this;
    }
    offset(dx, dy) {
        let x = dx, y = dy;
        if (dx instanceof Point) {
            x = dx.x;
            y = dx.y;
        }
        this.x += x;
        this.y += y;
        return this;
    }
    union(r) {
        const x1 = Math.min(this.x, r.x);
        const y1 = Math.min(this.y, r.y);
        const x2 = Math.max((this.x + this.width), (r.x + r.width));
        const y2 = Math.max((this.y + this.height), (r.y + r.height));
        return new Rect(x1, y1, x2 - x1, y2 - y1);
    }
    center() {
        return new Point(this.x + this.width / 2, this.y + this.height / 2);
    }
    top() {
        return new Point(this.x + this.width / 2, this.y);
    }
    right() {
        return new Point(this.x + this.width, this.y + this.height / 2);
    }
    bottom() {
        return new Point(this.x + this.width / 2, this.y + this.height);
    }
    left() {
        return new Point(this.x, this.y + this.height / 2);
    }
    topLeft() {
        return new Point(this.x, this.y);
    }
    topRight() {
        return new Point(this.x + this.width, this.y);
    }
    bottomLeft() {
        return new Point(this.x, this.y + this.height);
    }
    bottomRight() {
        return new Point(this.x + this.width, this.y + this.height);
    }
    clone() {
        return new Rect(this.x, this.y, this.width, this.height);
    }
    isEmpty() {
        return !this.width && !this.height;
    }
    equals(rect) {
        return this.x === rect.x && this.y === rect.y && this.width === rect.width && this.height === rect.height;
    }
    rotatedBounds(angle) {
        const rect = this.clone(), points = this.rotatedPoints(angle), tl = points[0], tr = points[1], br = points[2], bl = points[3];
        rect.x = Math.min(br.x, tl.x, tr.x, bl.x);
        rect.y = Math.min(br.y, tl.y, tr.y, bl.y);
        rect.width = Math.max(br.x, tl.x, tr.x, bl.x) - rect.x;
        rect.height = Math.max(br.y, tl.y, tr.y, bl.y) - rect.y;
        return rect;
    }
    rotatedPoints(angle) {
        const c = this.center(), br = this.bottomRight().rotate(angle, c), tl = this.topLeft().rotate(angle, c), tr = this.topRight().rotate(angle, c), bl = this.bottomLeft().rotate(angle, c);
        return [tl, tr, br, bl];
    }
    toString(delimiter) {
        delimiter = delimiter || ' ';
        return this.x + delimiter + this.y + delimiter + this.width + delimiter + this.height;
    }
    scale(scaleX, scaleY, staicPoint, adornerCenter, angle) {
        let tl = this.topLeft();
        const thisCenter = this.center();
        tl.rotate(angle, thisCenter).rotate(angle, adornerCenter);
        const delta = staicPoint.minus(tl);
        const scaled = new Point(delta.x * scaleX, delta.y * scaleY);
        const position = delta.minus(scaled);
        tl = tl.plus(position);
        tl.rotate(angle, adornerCenter).rotate(angle, thisCenter);
        this.x = tl.x;
        this.y = tl.y;
        this.width *= scaleX;
        this.height *= scaleY;
    }
    zoom(zoom) {
        this.x *= zoom;
        this.y *= zoom;
        this.width *= zoom;
        this.height *= zoom;
        return this;
    }
    overlaps(rect) {
        const bottomRight = this.bottomRight();
        const rectBottomRight = rect.bottomRight();
        const overlaps = !(bottomRight.x < rect.x || bottomRight.y < rect.y ||
            rectBottomRight.x < this.x || rectBottomRight.y < this.y);
        return overlaps;
    }
    static toRect(rect) {
        if (!(rect instanceof Rect)) {
            rect = new Rect(rect.x, rect.y, rect.width, rect.height);
        }
        return rect;
    }
    static empty() {
        return new Rect(0, 0, 0, 0);
    }
    static fromPoints(p, q) {
        if (isNaN(p.x) || isNaN(p.y) || isNaN(q.x) || isNaN(q.y)) {
            throw new Error('Some values are NaN.');
        }
        return new Rect(Math.min(p.x, q.x), Math.min(p.y, q.y), Math.abs(p.x - q.x), Math.abs(p.y - q.y));
    }
}
/**
 * Aligns two rectangles, where one is the container and the other is content.
 */
class RectAlign {
    constructor(container) {
        this.container = Rect.toRect(container);
    }
    align(content, alignment) {
        const alignValues = alignment.toLowerCase().split(' ');
        for (let i = 0; i < alignValues.length; i++) {
            content = this._singleAlign(content, alignValues[i]);
        }
        return content;
    }
    _singleAlign(content, alignment) {
        if (isFunction(this[alignment])) {
            return this[alignment](content);
        }
        else {
            return content;
        }
    }
    left(content) {
        return this._align(content, this._left);
    }
    center(content) {
        return this._align(content, this._center);
    }
    right(content) {
        return this._align(content, this._right);
    }
    stretch(content) {
        return this._align(content, this._stretch);
    }
    top(content) {
        return this._align(content, this._top);
    }
    middle(content) {
        return this._align(content, this._middle);
    }
    bottom(content) {
        return this._align(content, this._bottom);
    }
    _left(container, content) {
        content.x = container.x;
    }
    _center(container, content) {
        content.x = ((container.width - content.width) / 2) || 0;
    }
    _right(container, content) {
        content.x = container.width - content.width;
    }
    _top(container, content) {
        content.y = container.y;
    }
    _middle(container, content) {
        content.y = ((container.height - content.height) / 2) || 0;
    }
    _bottom(container, content) {
        content.y = container.height - content.height;
    }
    _stretch(container, content) {
        content.x = 0;
        content.y = 0;
        content.height = container.height;
        content.width = container.width;
    }
    _align(content, alignCalc) {
        content = Rect.toRect(content);
        alignCalc(this.container, content);
        return content;
    }
}

export { Point, Rect, RectAlign };
